import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { goalsApi, measurementsApi } from '@/shared/api/endpoints';
import { ApiError } from '@/shared/api/httpClient';
import type { BodyMeasurement, Goal, GoalProposal, GoalType } from '@/shared/api/types';
import { formatDayShort } from '@/shared/format/dates';
import { formatGrams, formatKcal } from '@/shared/format/nutrition';
import { describeError, useAsync } from '@/shared/hooks/useAsync';
import { useSession } from '@/shared/session/useSession';
import { Banner, Button, Card, Chip, Eyebrow, Segmented, Sheet, Spinner } from '@/shared/ui';
import { EsqueletoLista, useEsqueleto } from '@/shared/ui/Skeleton';

/** RN-14 · los cinco factores. Ningún valor intermedio: son los que el sistema sabe justificar. */
const ACTIVITY_FACTORS = [
  { value: 1.2, label: 'Sedentario' },
  { value: 1.375, label: 'Ligero' },
  { value: 1.55, label: 'Moderado' },
  { value: 1.725, label: 'Intenso' },
  { value: 1.9, label: 'Muy intenso' },
] as const;

const GOAL_TYPES = [
  { value: 'Deficit' as const, label: 'Déficit' },
  { value: 'Maintenance' as const, label: 'Mantener' },
  { value: 'Surplus' as const, label: 'Superávit' },
];

const GOAL_TYPE_CRITERION: Record<GoalType, string> = {
  Deficit: 'GET − 20 %',
  Maintenance: 'GET',
  Surplus: 'GET + 10 %',
};

/** RN-18 · de dónde sale cada macro. El criterio explica el número mejor que el número. */
const MACRO_CRITERIA = [
  { key: 'protein', name: 'Proteína', criterion: '1,8 g por kg de peso' },
  { key: 'carbs', name: 'Carbohidratos', criterion: 'lo que resta de las kcal' },
  { key: 'fat', name: 'Grasa', criterion: '25 % de las kcal · piso funcional' },
] as const;

const STEP_KCAL = 10;

export function GoalsPage() {
  const { profile } = useSession();
  const goals = useAsync<Goal[]>(() => goalsApi.list(), []);
  const measurements = useAsync<BodyMeasurement[]>(() => measurementsApi.history(), []);

  const [type, setType] = useState<GoalType>('Deficit');
  const [activityFactor, setActivityFactor] = useState<number>(1.375);
  const [proposal, setProposal] = useState<GoalProposal | null>(null);
  const [loadingProposal, setLoadingProposal] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const complete = profile === null || profile.isComplete;

  /*
   * Sin peso no hay propuesta, y eso se sabe acá antes de preguntar.
   *
   * El servidor rechaza la propuesta con un mensaje correcto, pero la pantalla lo pintaba como un
   * error rojo y sin salida: la persona quedaba mirando un cartel que le pedía una medición sin
   * decirle dónde cargarla. Un paso pendiente del alta no es una falla.
   *
   * Optimista mientras carga: mostrar el bloqueo antes de saber si hay mediciones haría parpadear
   * un cartel de bloqueo en la cara de alguien que sí las tiene.
   */
  const hasWeight = measurements.data === null || measurements.data.length > 0;

  /* La propuesta se recalcula sola cuando cambia el tipo o el factor: pedirle a alguien que
     apriete "calcular" después de mover un selector es hacerle confirmar lo que ya eligió. */
  useEffect(() => {
    if (!complete || !hasWeight) {
      return;
    }

    let active = true;
    setLoadingProposal(true);
    setError(null);

    goalsApi
      .proposal(type, activityFactor)
      .then((next) => {
        if (active) {
          setProposal(next);
        }
      })
      .catch((cause: unknown) => {
        if (active) {
          setError(describeError(cause));
          setProposal(null);
        }
      })
      .finally(() => {
        if (active) {
          setLoadingProposal(false);
        }
      });

    return () => {
      active = false;
    };
  }, [type, activityFactor, complete, hasWeight]);

  // Antes de los cortes tempranos: un hook no puede quedar detrás de un return.
  const esqueleto = useEsqueleto(goals.loading);

  if (!complete) {
    return (
      <>
        <h1>Tu objetivo</h1>
        <Banner tone="decision">
          Sin sexo, fecha de nacimiento y altura no se puede calcular tu gasto.{' '}
          <Link to="/perfil">Completar el perfil</Link>
        </Banner>
      </>
    );
  }

  if (!hasWeight) {
    return (
      <>
        <h1>Tu objetivo</h1>
        <Banner tone="decision">
          Para calcular tu gasto hace falta tu peso. Es el único dato que falta.{' '}
          <Link to="/mediciones">Cargar mi peso</Link>
        </Banner>
      </>
    );
  }

  const currentGoal = goals.data?.find((goal) => goal.isCurrent) ?? null;

  return (
    <>
      <header>
        {currentGoal !== null && (
          <Eyebrow>Vigente desde el {formatDayShort(currentGoal.from)}</Eyebrow>
        )}
        <h1>Tu objetivo</h1>
      </header>

      {error !== null && <Banner tone="danger">{error}</Banner>}

      <Segmented options={GOAL_TYPES} value={type} onChange={setType} label="Tipo de objetivo" />

      <div className="stack" style={{ gap: 'var(--space-2)' }}>
        <Eyebrow>Nivel de actividad</Eyebrow>
        <div className="row" style={{ flexWrap: 'wrap' }}>
          {ACTIVITY_FACTORS.map((factor) => (
            <Chip
              key={factor.value}
              large
              aria-pressed={factor.value === activityFactor}
              onClick={() => setActivityFactor(factor.value)}
            >
              {factor.label}
            </Chip>
          ))}
        </div>
      </div>

      {loadingProposal && proposal === null && <Spinner label="Calculando" />}

      {proposal !== null && (
        <ProposalEditor
          key={`${type}-${activityFactor}`}
          proposal={proposal}
          type={type}
          activityFactor={activityFactor}
          onSaved={goals.reload}
        />
      )}

      {currentGoal !== null && (
        // RF-33 · el anterior se cierra el día previo. Decirlo antes evita que alguien crea que
        // va a tener dos vigentes o que pierde el historial.
        <p className="micro muted">
          Guardar uno nuevo cierra el actual el día anterior. El historial se conserva y los
          análisis pasados se siguen evaluando contra el objetivo que estaba vigente entonces.
        </p>
      )}

      <section className="stack">
        <h2>Historial</h2>
        {esqueleto && goals.data === null && (
          <EsqueletoLista etiqueta="Cargando los objetivos" filas={3} />
        )}
        {goals.data !== null && goals.data.length > 0 && (
          <Card size="flush">
            {goals.data.map((goal) => (
              <div key={goal.id} className="item-row" style={{ cursor: 'default' }}>
                <span className="item-body">
                  <span className="item-name">
                    {GOAL_TYPES.find((entry) => entry.value === goal.type)?.label ?? goal.type}
                  </span>
                  <span className="item-meta">
                    {formatDayShort(goal.from)} —{' '}
                    {goal.to === null ? 'vigente' : formatDayShort(goal.to)}
                  </span>
                </span>
                <span className="item-kcal">{formatKcal(goal.targetKcal)} kcal</span>
              </div>
            ))}
          </Card>
        )}
      </section>
    </>
  );
}

function ProposalEditor({
  proposal,
  type,
  activityFactor,
  onSaved,
}: {
  proposal: GoalProposal;
  type: GoalType;
  activityFactor: number;
  onSaved: () => void;
}) {
  const [targetKcal, setTargetKcal] = useState(proposal.proposedKcal);
  const [proteinG, setProteinG] = useState(proposal.macros.proteinG.toString());
  const [carbohydratesG, setCarbohydratesG] = useState(proposal.macros.carbohydratesG.toString());
  const [fatG, setFatG] = useState(proposal.macros.fatG.toString());

  /*
   * RF-31 · la propuesta es un valor por defecto, no una decisión. Mientras nadie la toque se
   * mandan nulos y el servidor usa lo que él mismo calculó, en vez de que el cliente le devuelva
   * su propio número redondeado por el camino.
   */
  const [touched, setTouched] = useState(false);

  const [belowFloor, setBelowFloor] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const macros = [proteinG, carbohydratesG, fatG];
  const setters = [setProteinG, setCarbohydratesG, setFatG];

  const save = async (confirmBelowFloor: boolean) => {
    setBusy(true);
    setError(null);

    try {
      await goalsApi.create({
        type,
        activityFactor,
        targetKcal: touched ? targetKcal : null,
        macros: touched
          ? {
              proteinG: Number(proteinG),
              carbohydratesG: Number(carbohydratesG),
              fatG: Number(fatG),
            }
          : null,
        confirmBelowFloor,
      });

      setBelowFloor(null);
      onSaved();
    } catch (cause) {
      /*
       * RF-34 · el rechazo ES la advertencia. El piso depende del sexo del perfil y lo resuelve el
       * servidor; una copia de esa regla acá sería una segunda implementación destinada a
       * divergir. La interfaz muestra el mensaje que vino, que conoce el piso que se aplicó.
       */
      if (cause instanceof ApiError && cause.statusCode === 422) {
        setBelowFloor(cause.messages.join(' '));
        return;
      }

      setError(describeError(cause));
    } finally {
      setBusy(false);
    }
  };

  const adjust = (delta: number) => {
    setTargetKcal((current) => Math.max(0, current + delta));
    setTouched(true);
  };

  return (
    <>
      <Card size="lg">
        <div className="row-between">
          <Button
            aria-label="Bajar diez kilocalorías"
            style={{ width: 44, minHeight: 44, padding: 0 }}
            onClick={() => adjust(-STEP_KCAL)}
          >
            −
          </Button>

          <div className="stack" style={{ alignItems: 'center', gap: 2 }}>
            <span
              className="numeric"
              style={{ fontSize: 'var(--text-figure)', fontWeight: 800, lineHeight: 1 }}
            >
              {formatKcal(targetKcal)}
            </span>
            <span className="nano muted numeric">
              propuesto {formatKcal(proposal.proposedKcal)} · {GOAL_TYPE_CRITERION[type]}
            </span>
          </div>

          <Button
            aria-label="Subir diez kilocalorías"
            style={{ width: 44, minHeight: 44, padding: 0 }}
            onClick={() => adjust(STEP_KCAL)}
          >
            +
          </Button>
        </div>

        <hr className="divider" />

        {/* RF-32 · la fórmula y el factor quedan a la vista: un número sin origen no se discute. */}
        <div className="stack" style={{ gap: 'var(--space-2)' }}>
          <div className="row-between micro">
            <span className="muted">TMB · {proposal.tmbFormula}</span>
            <span className="numeric">{formatKcal(Number(proposal.tmbKcal))} kcal</span>
          </div>
          <div className="row-between micro">
            <span className="muted">GET · factor {proposal.activityFactor}</span>
            <span className="numeric">
              {formatKcal(Number(proposal.totalExpenditureKcal))} kcal
            </span>
          </div>
          <span className="nano muted">
            Calculado con un peso de {formatGrams(proposal.referenceWeightKg)} kg.
          </span>
        </div>

        {/* RN-08 · el piso calórico no se cruza sin decirlo en voz alta. */}
        {proposal.raisedToFloor && (
          <Banner tone="decision">
            El cálculo daba por debajo del piso de {formatKcal(proposal.calorieFloor)} kcal, así que
            la propuesta se subió hasta ahí.
          </Banner>
        )}
      </Card>

      <div className="stack" style={{ gap: 'var(--space-2)' }}>
        <Eyebrow>Macros propuestos</Eyebrow>
        <Card size="flush">
          {MACRO_CRITERIA.map((macro, index) => (
            <label
              key={macro.key}
              className={`option-row macro-${macro.key}`}
              style={{ cursor: 'text' }}
            >
              <span className="macro-rule" style={{ width: 3, height: 26, flex: 'none' }} />
              <span className="stack" style={{ flex: 1, gap: 2 }}>
                <span className="option-title">{macro.name}</span>
                <span className="option-hint">{macro.criterion}</span>
              </span>
              <input
                type="number"
                inputMode="decimal"
                min={0}
                step="any"
                value={macros[index]}
                onChange={(event) => {
                  setters[index]?.(event.target.value);
                  setTouched(true);
                }}
                style={{
                  width: 84,
                  height: 42,
                  border: '1px solid var(--rule)',
                  borderRadius: 'var(--radius-control)',
                  background: 'var(--surface)',
                  textAlign: 'right',
                  font: 'inherit',
                  fontWeight: 700,
                  fontVariantNumeric: 'tabular-nums',
                  color: 'inherit',
                  paddingInline: 'var(--space-2)',
                }}
              />
              <span className="micro muted" style={{ width: 14 }}>
                g
              </span>
            </label>
          ))}
        </Card>
      </div>

      {error !== null && <Banner tone="danger">{error}</Banner>}

      <Button variant="primary" block disabled={busy} onClick={() => void save(false)}>
        {busy ? 'Guardando…' : 'Guardar el objetivo'}
      </Button>

      {belowFloor !== null && (
        <Sheet
          title={`${formatKcal(targetKcal)} kcal está por debajo del piso`}
          onClose={() => setBelowFloor(null)}
        >
          <p className="small" style={{ lineHeight: 1.5 }}>
            {belowFloor}
          </p>
          <p className="small muted" style={{ lineHeight: 1.5 }}>
            El sistema no propone valores por debajo de ese piso. Sostener uno menor durante un
            tiempo prolongado requiere supervisión profesional, que esta aplicación no reemplaza.
          </p>

          <div className="row-between small numeric">
            <span className="muted">Propuesto</span>
            <span>{formatKcal(proposal.proposedKcal)} kcal</span>
          </div>
          <div className="row-between small numeric">
            <span className="muted">Lo que ingresaste</span>
            <span>{formatKcal(targetKcal)} kcal</span>
          </div>

          {/*
            La salida segura es la primaria y no lleva rojo ni ícono de alarma: es una advertencia
            seria, no un susto.
          */}
          <Button
            variant="primary"
            block
            onClick={() => {
              setTargetKcal(proposal.proposedKcal);
              setBelowFloor(null);
            }}
          >
            Volver a {formatKcal(proposal.proposedKcal)} kcal
          </Button>
          <Button variant="quiet" block disabled={busy} onClick={() => void save(true)}>
            Entiendo el riesgo, fijar {formatKcal(targetKcal)}
          </Button>
        </Sheet>
      )}
    </>
  );
}
